/**
 * Unit Conversion Utilities
 *
 * Provides conversion functions for weight, water, and height based on user preferences.
 * Values are stored in platform standard units (lbs, oz, in) and converted at display time.
 *
 * Usage:
 *   import { formatWeight, formatWater, useUnitPreferences } from '../utils/unitConversions';
 *
 *   // In component:
 *   const prefs = useUnitPreferences();
 *   const displayWeight = formatWeight(370.5, prefs.units.weight); // "370.5 lbs" or "168.1 kg"
 */

// Types
export interface UnitPreferences {
  units: {
    weight: 'lbs' | 'kg';
    water: 'oz' | 'ml' | 'L';
    height: 'in' | 'cm';
  };
  source: 'system' | 'user';
}

// Default preferences (fallback if API fails)
const DEFAULT_PREFERENCES: UnitPreferences = {
  units: {
    weight: 'lbs',
    water: 'oz',
    height: 'in'
  },
  source: 'system'
};

// Cached preferences
let cachedPreferences: UnitPreferences | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch preferences from API with caching
 */
export async function fetchPreferences(): Promise<UnitPreferences> {
  const now = Date.now();

  // Return cached if still valid
  if (cachedPreferences && (now - cacheTimestamp) < CACHE_TTL) {
    return cachedPreferences;
  }

  try {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch('/api/preferences', { headers });

    if (response.ok) {
      cachedPreferences = await response.json();
      cacheTimestamp = now;
      return cachedPreferences!;
    }
  } catch (error) {
    console.error('Failed to fetch preferences:', error);
  }

  // Return defaults on failure
  return DEFAULT_PREFERENCES;
}

/**
 * Get cached preferences synchronously (for initial render)
 * Returns defaults if not yet fetched
 */
export function getPreferences(): UnitPreferences {
  return cachedPreferences || DEFAULT_PREFERENCES;
}

/**
 * Clear preference cache (call on logout or preference update)
 */
export function clearPreferenceCache(): void {
  cachedPreferences = null;
  cacheTimestamp = 0;
}

// ============================================
// CONVERSION FUNCTIONS
// ============================================

/**
 * Convert weight from platform standard (lbs) to target unit
 */
export function convertWeight(lbs: number, toUnit: 'lbs' | 'kg'): number {
  if (toUnit === 'kg') {
    return Math.round((lbs / 2.20462) * 10) / 10; // 1 decimal
  }
  return Math.round(lbs * 10) / 10;
}

/**
 * Convert weight from input unit to platform standard (lbs)
 */
export function convertWeightToStorage(value: number, fromUnit: 'lbs' | 'kg'): number {
  if (fromUnit === 'kg') {
    return Math.round(value * 2.20462 * 10) / 10;
  }
  return value;
}

/**
 * Convert water from platform standard (oz) to target unit
 */
export function convertWater(oz: number, toUnit: 'oz' | 'ml' | 'L'): number {
  if (toUnit === 'ml') {
    return Math.round(oz * 29.5735);
  }
  if (toUnit === 'L') {
    return Math.round((oz * 0.0295735) * 10) / 10; // 1 decimal
  }
  return Math.round(oz);
}

/**
 * Convert water from input unit to platform standard (oz)
 */
export function convertWaterToStorage(value: number, fromUnit: 'oz' | 'ml' | 'L'): number {
  if (fromUnit === 'ml') {
    return Math.round((value / 29.5735) * 10) / 10;
  }
  if (fromUnit === 'L') {
    return Math.round((value / 0.0295735) * 10) / 10;
  }
  return value;
}

/**
 * Convert height from platform standard (in) to target unit
 */
export function convertHeight(inches: number, toUnit: 'in' | 'cm'): number {
  if (toUnit === 'cm') {
    return Math.round(inches * 2.54);
  }
  return inches;
}

/**
 * Convert height from input unit to platform standard (in)
 */
export function convertHeightToStorage(value: number, fromUnit: 'in' | 'cm'): number {
  if (fromUnit === 'cm') {
    return Math.round((value / 2.54) * 10) / 10;
  }
  return value;
}

// ============================================
// FORMATTING FUNCTIONS
// ============================================

/**
 * Format weight for display with unit label
 */
export function formatWeight(
  lbs: number | undefined | null,
  unit: 'lbs' | 'kg' = 'lbs',
  options: { showUnit?: boolean; decimals?: number } = {}
): string {
  const { showUnit = true, decimals = 1 } = options;

  if (lbs === undefined || lbs === null) {
    return showUnit ? `-- ${unit}` : '--';
  }

  const converted = convertWeight(lbs, unit);
  const formatted = converted.toFixed(decimals);

  return showUnit ? `${formatted} ${unit}` : formatted;
}

/**
 * Format water for display with unit label
 */
export function formatWater(
  oz: number | undefined | null,
  unit: 'oz' | 'ml' | 'L' = 'oz',
  options: { showUnit?: boolean } = {}
): string {
  const { showUnit = true } = options;

  if (oz === undefined || oz === null) {
    return showUnit ? `-- ${unit}` : '--';
  }

  const converted = convertWater(oz, unit);
  const formatted = unit === 'L' ? converted.toFixed(1) : converted.toString();

  return showUnit ? `${formatted} ${unit}` : formatted;
}

/**
 * Format height for display with unit label
 */
export function formatHeight(
  inches: number | undefined | null,
  unit: 'in' | 'cm' = 'in',
  options: { showUnit?: boolean; asFeetInches?: boolean } = {}
): string {
  const { showUnit = true, asFeetInches = true } = options;

  if (inches === undefined || inches === null) {
    return showUnit ? `-- ${unit}` : '--';
  }

  if (unit === 'in' && asFeetInches) {
    const feet = Math.floor(inches / 12);
    const remainingInches = Math.round(inches % 12);
    return `${feet}'${remainingInches}"`;
  }

  const converted = convertHeight(inches, unit);

  return showUnit ? `${converted} ${unit}` : converted.toString();
}

// ============================================
// REACT HOOK (optional - for components)
// ============================================

import { useState, useEffect } from 'react';

/**
 * React hook to get unit preferences
 * Fetches from API and caches result
 */
export function useUnitPreferences(): UnitPreferences {
  const [preferences, setPreferences] = useState<UnitPreferences>(getPreferences());

  useEffect(() => {
    fetchPreferences().then(setPreferences);
  }, []);

  return preferences;
}
